import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { confirmDialog } from 'primereact/confirmdialog';
import { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  selectedTickerFilterAtom,
  portfolioNameAtom,
} from '~/atoms/dashboardAtom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { WidgetHeader } from '../../common/WidgetHeader';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';
import { AllocationAnalysisDialog } from '../AllocationWidget/AllocationAnalysisDialog';
import { createAgTheme } from '~/components/ui/tables/ag/agTheme';
import { siteThemeMode } from '~/atoms/userPreferencesAtoms';

interface CSVAllocationData {
  isin: string;
  current_allocation: number;
  sector: string;
  ticker?: string;
  name?: string;
}

export interface PlainTableRow {
  full_ticker: string;
  isin: string;
  current_holdings: number | null;
  proposed_holdings: number | null;
  company_name?: string;
}

export type PlainTableWidgetProps = {
  data: PlainTableRow[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string;
  onCSVUpload?: (
    csvData: CSVAllocationData[],
    description: string,
    batchType: 'universe' | 'benchmark' | 'current_holdings' | 'proposed',
  ) => Promise<void>;
  primaryColumnLabel?: string;
  comparisonColumnLabel?: string;
  primaryBatchType?: 'current_holdings' | 'universe' | 'benchmark' | 'proposed';
  comparisonBatchType?:
    | 'current_holdings'
    | 'universe'
    | 'benchmark'
    | 'proposed';
  onBatchTypeChange?: (
    batchType: 'current_holdings' | 'universe' | 'benchmark' | 'proposed',
  ) => void;
  onDelete?: () => Promise<void>;
};

export const PlainTableWidget: React.FC<PlainTableWidgetProps> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'Current Holdings',
  snapshotId,
  onCSVUpload,
  primaryColumnLabel = 'Current Holdings',
  comparisonColumnLabel = 'AI Portfolio',
  primaryBatchType = 'current_holdings',
  comparisonBatchType = 'proposed',
  onBatchTypeChange,
  onDelete,
}) => {
  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const gridRef = useRef<AgGridReact<PlainTableRow>>(null);
  const setSelectedTickerFilter = useSetAtom(selectedTickerFilterAtom);
  const themeMode = useAtomValue(siteThemeMode);
  const gridTheme = useMemo(() => createAgTheme(themeMode), [themeMode]);
  const portfolioName = useAtomValue(portfolioNameAtom);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: PlainTableWidget,
    title: title || 'Allocation Table',
    defaultWidth: 1200,
    defaultHeight: 800,
    widgetProps: {
      data,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      snapshotId,
      onCSVUpload,
      primaryBatchType,
      comparisonBatchType,
      onBatchTypeChange,
      onDelete,
      primaryColumnLabel,
      comparisonColumnLabel,
    },
  });

  const handlePopoutWrapper = useCallback(
    (widgetId: string) => {
      handlePopout?.(widgetId);
    },
    [handlePopout],
  );

  const handleUploadComplete = useCallback(
    async (
      csvData: CSVAllocationData[],
      description: string,
      batchType: 'universe' | 'benchmark' | 'current_holdings' | 'proposed',
    ) => {
      if (onCSVUpload) {
        await onCSVUpload(csvData, description, batchType);
      }
    },
    [onCSVUpload],
  );

  const batchTypeLabel = useMemo(() => {
    switch (primaryBatchType) {
      case 'current_holdings':
        return 'current holdings';
      case 'universe':
        return 'reference universe';
      case 'benchmark':
        return 'benchmark';
      case 'proposed':
        return 'proposed holdings';
      default:
        return 'data';
    }
  }, [primaryBatchType]);

  const handleDeleteClick = useCallback(() => {
    if (!onDelete) return;
    const isCurrentHoldings = primaryBatchType === 'current_holdings';
    confirmDialog({
      message: isCurrentHoldings
        ? `This will delete your ${batchTypeLabel} and the analysis results. You can upload a new file and run Analysis again afterward. Are you sure?`
        : `This will delete your ${batchTypeLabel}. You can upload a new file and run Analysis again afterward. Are you sure?`,
      header: `Delete ${batchTypeLabel}`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        setIsDeleting(true);
        try {
          await onDelete();
        } finally {
          setIsDeleting(false);
        }
      },
    });
  }, [onDelete, batchTypeLabel, primaryBatchType]);

  // Format percentage values
  const percentageFormatter = (params: { value: number | null }) => {
    if (params.value === null || params.value === undefined) {
      return '0.00%';
    }
    return `${params.value.toFixed(2)}%`;
  };

  // Format delta values with +/- sign
  const deltaFormatter = (params: { data: PlainTableRow }) => {
    const current = params.data.current_holdings ?? 0;
    const proposed = params.data.proposed_holdings ?? 0;

    const delta = proposed - current;
    const sign = delta > 0 ? '+' : '';
    return `${sign}${delta.toFixed(2)}%`;
  };

  // Company name cell renderer component
  const CompanyNameCellRenderer = (props: {
    value: string;
    data: PlainTableRow;
  }) => {
    const displayName = props.data?.company_name || props.value;
    const ticker = props.value;

    return (
      <span
        className="ticker-cell-clickable ticker-tooltip-target"
        onClick={() => setSelectedTickerFilter(ticker)}
        data-pr-tooltip={ticker}
        style={{
          color: 'var(--bq-color-info-strong)',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        {displayName}
      </span>
    );
  };

  // Style delta cells with color
  const deltaStyler = (params: { data: PlainTableRow }) => {
    const current = params.data.current_holdings ?? 0;
    const proposed = params.data.proposed_holdings ?? 0;

    const delta = proposed - current;

    if (delta > 0) {
      return { color: 'var(--bq-color-positive)', fontWeight: '600' }; // positive
    } else if (delta < 0) {
      return { color: 'var(--bq-color-negative)', fontWeight: '600' }; // negative
    }
    return { color: 'var(--bq-color-neutral)' }; // neutral for zero
  };

  // AG Grid column definitions
  const columnDefs = useMemo<ColDef<PlainTableRow>[]>(
    () => [
      {
        field: 'full_ticker',
        headerName: 'Company',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 180,
        flex: 1,
        headerClass: 'ag-left-aligned-header',
        cellRenderer: CompanyNameCellRenderer,
        valueGetter: (params) =>
          params.data?.company_name || params.data?.full_ticker || '',
        cellStyle: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      {
        field: 'isin',
        headerName: 'ISIN',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 150,
        flex: 1,
        headerClass: 'ag-left-aligned-header',
      },
      {
        field: 'current_holdings',
        headerName: `${primaryColumnLabel} (%)`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 180,
        flex: 1,
        valueFormatter: percentageFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell flex justify-content-center',
      },
      {
        field: 'proposed_holdings',
        headerName: `${comparisonColumnLabel} (%)`,
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 180,
        flex: 1,
        valueFormatter: percentageFormatter,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell flex justify-content-center',
      },
      {
        field: 'delta',
        headerName: 'Delta (%)',
        sortable: true,
        filter: 'agNumberColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        minWidth: 120,
        flex: 1,
        sort: 'desc', // Sort by delta descending by default
        valueGetter: (params) => {
          const current = params.data?.current_holdings ?? 0;
          const proposed = params.data?.proposed_holdings ?? 0;
          return proposed - current;
        },
        valueFormatter: deltaFormatter,
        cellStyle: deltaStyler,
        type: 'numericColumn',
        headerClass: 'ag-left-aligned-header',
        cellClass: 'percentageCell flex justify-content-center',
      },
    ],
    [primaryColumnLabel, comparisonColumnLabel],
  );

  // Default column properties
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      enableCellChangeFlash: true,
      cellClass: 'flex align-items-center',
    }),
    [],
  );

  // Excel export styles
  const excelStyles = useMemo(
    () => [
      {
        id: 'percentageCell',
        dataType: 'number',
        numberFormat: { format: '0.00%' },
      },
    ],
    [],
  );

  // Export to CSV
  // Handle grid resize when container size changes
  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current?.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    };

    // Create a ResizeObserver to watch for container size changes
    const widgetElement = document.getElementById(widgetId);
    if (widgetElement) {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(widgetElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [widgetId]);

  const handleExportCSV = useCallback(() => {
    gridRef.current?.api.exportDataAsExcel({
      fileName: `allocations_${snapshotId || 'export'}.xlsx`,
      processCellCallback: (params) => {
        const colId = params.column.getColId();

        // For percentage columns, get raw value from data and convert to decimal
        if (colId === 'current_holdings' || colId === 'proposed_holdings') {
          // Get raw value from data (not formatted value)
          const rawValue = params.node?.data?.[colId];
          if (rawValue === null || rawValue === undefined) {
            return 0;
          }
          // Convert percentage to decimal (5.23 -> 0.0523)
          return rawValue / 100;
        }

        // For delta column, calculate from raw data
        if (colId === 'delta') {
          const current = params.node?.data?.current_holdings ?? 0;
          const proposed = params.node?.data?.proposed_holdings ?? 0;
          const delta = proposed - current;
          // Convert to decimal
          return delta / 100;
        }

        return params.value;
      },
    });
  }, [snapshotId]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget || undefined}
          refetch={() => refetch()}
          onPopout={handlePopoutWrapper}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget || undefined}
          onPopout={handlePopoutWrapper}
          widgetId={widgetId}
          refetch={() => refetch()}
          children={
            <Button
              size="small"
              className="text-black"
              onClick={() => setUploadDialogVisible(true)}
              disabled={!onCSVUpload}
              style={{ padding: '0.4375rem 0.65625rem' }}
            >
              New
            </Button>
          }
        />
        <NoDataAvailableTemplate
          icon="pi pi-inbox"
          title="No snapshot selected"
          subTitle="Please upload a CSV to create a snapshot"
        />
        {onCSVUpload && (
          <AllocationAnalysisDialog
            visible={uploadDialogVisible}
            onHide={() => setUploadDialogVisible(false)}
            onDataProcessed={handleUploadComplete}
            initialBatchType={primaryBatchType}
          />
        )}
      </div>
    );
  }

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <Tooltip target=".ticker-tooltip-target" position="top" />
      <WidgetHeader
        title={title}
        removeWidget={removeWidget || undefined}
        onPopout={handlePopoutWrapper}
        widgetId={widgetId}
        refetch={() => refetch()}
        children={
          <div className="flex gap-2 ml-2 align-items-center">
            {onBatchTypeChange && (
              <Dropdown
                value={primaryBatchType}
                onChange={(e) => onBatchTypeChange(e.value)}
                options={[
                  { label: 'Current Holdings', value: 'current_holdings' },
                  { label: 'Reference Universe', value: 'universe' },
                  { label: 'Benchmark', value: 'benchmark' },
                ]}
                pt={{
                  root: {
                    style: {
                      height: '36px',
                      fontSize: '0.875rem',
                    },
                  },
                  input: {
                    style: {
                      padding: '0.5rem 0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      height: '100%',
                    },
                  },
                }}
              />
            )}
            {data.length > 0 && (
              <Button
                size="small"
                icon="pi pi-download"
                className="text-black"
                onClick={handleExportCSV}
                tooltip="Export to XLSX"
                tooltipOptions={{ position: 'bottom' }}
                style={{ padding: '0.4375rem 0.65625rem' }}
              />
            )}
            {onDelete && (
              <Button
                size="small"
                icon={isDeleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
                className="text-black"
                disabled={isDeleting}
                onClick={handleDeleteClick}
                tooltip={`Delete ${batchTypeLabel}`}
                tooltipOptions={{ position: 'bottom' }}
                style={{ padding: '0.4375rem 0.65625rem' }}
              />
            )}
            {onCSVUpload && (
              <Button
                size="small"
                className="text-black"
                onClick={() => setUploadDialogVisible(true)}
                style={{ padding: '0.4375rem 0.65625rem' }}
              >
                New
              </Button>
            )}
          </div>
        }
      />

      <div className="flex-1 overflow-hidden p-3">
        <Card
          className="h-full"
          pt={{
            root: {
              className: 'h-full',
              style: {
                borderRadius: '0.5rem',
                overflow: 'hidden',
              },
            },
            body: {
              className: 'h-full p-0',
              style: {
                borderRadius: '0.5rem',
                overflow: 'hidden',
              },
            },
            content: {
              className: 'h-full p-0',
            },
          }}
        >
          <div style={{ height: '100%', width: '100%' }}>
            {data.length === 0 ? (
              <div className="flex align-items-center justify-content-center h-full">
                <div
                  className="text-center"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  <i className="pi pi-inbox text-4xl mb-3 block"></i>
                  <p
                    className="text-xl"
                    style={{ color: 'var(--bq-text-primary)' }}
                  >
                    No allocation data available
                  </p>
                  <p
                    className="text-sm mt-2"
                    style={{ color: 'var(--bq-text-tertiary)' }}
                  >
                    Upload a CSV file to view allocations
                  </p>
                </div>
              </div>
            ) : (
              <AgGridReact<PlainTableRow>
                ref={gridRef}
                rowData={data}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                excelStyles={excelStyles}
                theme={gridTheme}
                loadThemeGoogleFonts={true}
                suppressCellFocus={false}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                onGridReady={() => {
                  gridRef.current?.api.sizeColumnsToFit();
                  // Set default filter to hide rows where delta = 0
                  gridRef.current?.api.setColumnFilterModel('delta', {
                    filterType: 'number',
                    type: 'notEqual',
                    filter: 0,
                  });
                  gridRef.current?.api.onFilterChanged();
                }}
                autoSizeStrategy={{
                  type: 'fitGridWidth',
                  defaultMinWidth: 100,
                }}
                // Enable clipboard operations
                enableRangeSelection={true}
                // Enable CSV export
                defaultCsvExportParams={{
                  fileName: `allocations_${snapshotId || 'export'}.csv`,
                }}
              />
            )}
          </div>
        </Card>
      </div>

      {onCSVUpload && (
        <AllocationAnalysisDialog
          visible={uploadDialogVisible}
          onHide={() => setUploadDialogVisible(false)}
          onDataProcessed={handleUploadComplete}
          initialBatchType={primaryBatchType}
        />
      )}
    </div>
  );
};
